import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ActionIcon, Avatar, Box, Button, Divider as MantineDivider, Flex, HoverCard, Paper, Skeleton, Text, Tooltip } from "@mantine/core";
import { IconUser, IconExternalLink, IconCheck, IconX } from "@tabler/icons";
import { BagelAIStarIcon } from "../../icons/x-symbol-svgrepo-com";
import { getInitials } from "../../utils/getInitials";
import { getIdea } from "../../Api";
import useEvidenceActions from "../../hooks/useEvidenceActions";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { showNotification } from "@mantine/notifications";

interface AssignedToIdeaTagProps {
  evidence: any;
  compact?: boolean;
}

// ── Case enum ────────────────────────────────────────────────────────────────
enum AssignmentCase {
  User = "user",
  Pm = "pm",
  AiAssign = "ai_assign",
  AiCreate = "ai_create",
  CannotDecide = "cannot_decide",
  InReview = "in_review",
}

/** Idea refs arrive as an id string or as a populated idea object; return the id either way. */
function toIdeaId(raw: unknown): string | undefined {
  if (typeof raw === "string") return raw || undefined;
  if (raw && typeof raw === "object" && "_id" in raw) return String((raw as { _id: unknown })._id);
  return undefined;
}

interface CaseDisplay {
  caseType: AssignmentCase;
  icon: React.ReactNode;
  tagLabel: string;
  popupHeader: string;
}

/**
 * Compute all display info for the tag in one shot.
 *
 * No autoAssignDecision:
 *   - owner exists  → User         (Assign By User)
 *   - no owner      → Pm           (Assign By PM)
 *
 * autoAssignDecision present:
 *   - type: "assign"                → AiAssign     (Assign By AI → existing idea)
 *   - type: "create_new"            → AiCreate     (Assign By AI → new idea created)
 *   - type: "cannot_decide"         → CannotDecide (Multiple Matches)
 *   - type: "decline_not_a_request" → InReview     (Not A Product Gap)
 */
function getCaseDisplay(
  evidence: any,
  pageIdeaId?: string,
): CaseDisplay | null {
  const ad = evidence?.autoAssignDecision;
  const state = evidence?.state;
  const owner = evidence?.owner;
  const resolvedOwnerName = owner?.name || owner?.username || owner?.email || "";

  const ideaId = toIdeaId(evidence?.ideaId);
  const onThisPage = !!(ideaId && ideaId === pageIdeaId);

  let caseType: AssignmentCase;

  if (!ad) {
    if (state !== "validated") return null;
    caseType = resolvedOwnerName ? AssignmentCase.User : AssignmentCase.Pm;
  } else {
    switch (ad?.type) {
    case "assign":
      if (state !== "validated") return null;
      caseType = AssignmentCase.AiAssign;
      break;
    case "create_new":
      // Only show when the idea was actually created and linked (state validated + ideaId present)
      if (state !== "validated" || !toIdeaId(evidence?.ideaId)) return null;
      caseType = AssignmentCase.AiCreate;
      break;
    case "cannot_decide":
      // Stop showing once the evidence has been assigned (state moves away from inReview)
      if (state === "validated") return null;
      caseType = AssignmentCase.CannotDecide;
      break;
    case "decline_not_a_request":
      // Stop showing if a later validate overrode the decline
      if (state === "validated") return null;
      caseType = AssignmentCase.InReview;
      break;
    default:
      if (state !== "validated") return null;
      caseType = resolvedOwnerName ? AssignmentCase.User : AssignmentCase.Pm;
    }
  }

  const isGap = caseType === AssignmentCase.CannotDecide;
  const isInReview = caseType === AssignmentCase.InReview;

  const icon: React.ReactNode =
    isGap || caseType === AssignmentCase.AiAssign || caseType === AssignmentCase.AiCreate ? (
      <BagelAIStarIcon width={14} height={14} style={{ flexShrink: 0 }} />
    ) : isInReview ? (
      <BagelAIStarIcon size={14} color="#6B7280" style={{ flexShrink: 0 }} />
    ) : caseType === AssignmentCase.User ? (
      <Avatar src={owner?.picture || ""} radius="xl" size={16} style={{ flexShrink: 0 }}>
        {getInitials(resolvedOwnerName)}
      </Avatar>
    ) : (
      <Avatar radius="xl" size={16} style={{ flexShrink: 0, background: "#E5E7EB" }}>
        <IconUser size={10} color="#6B7280" />
      </Avatar>
    );

  const tagLabel =
    isGap || isInReview
      ? "In Review"
      : onThisPage
        ? "Assigned to this Idea"
        : "Assigned to Idea";

  const popupHeader =
    isGap || isInReview
      ? "Multiple Matches Found"
      : caseType === AssignmentCase.AiAssign || caseType === AssignmentCase.AiCreate
        ? onThisPage
          ? "Assigned to this Idea by Bagel AI"
          : "Assigned to Idea by Bagel AI"
        : caseType === AssignmentCase.User
          ? `Assigned to Idea by ${resolvedOwnerName}`
          : "Assigned to Idea by PM";

  return { caseType, icon, tagLabel, popupHeader };
}

/**
 * Whether a caller should render the tag for this evidence.
 * Delegates to getCaseDisplay so the two predicates are always in sync.
 */
export function shouldShowAssignedTag(evidence: any): boolean {
  return getCaseDisplay(evidence) !== null;
}

// ── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fz="11px" fw={600} color="#9CA3AF" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
    {children}
  </Text>
);

const Divider: React.FC = () => (
  <Box sx={{ height: "1px", background: "#F3F4F6", margin: "10px 0" }} />
);

const ReasonBlock: React.FC<{ reason: string; withDivider?: boolean }> = ({ reason, withDivider = true }) => (
  <>
    {withDivider && <Divider />}
    <SectionLabel>Why</SectionLabel>
    <Box sx={{ maxHeight: "calc(5 * 13px * 1.6)", overflowY: "auto" }}>
      <Text fz="13px" color="#374151" lh={1.6}>{reason}</Text>
    </Box>
  </>
);

const IdeaLinkRow: React.FC<{
  ideaId: string;
  idea?: any;
  evidenceId?: string;
  singleLine?: boolean;
  linkable?: boolean;
  onAssign?: (ideaId: string, ideaTitle: string) => void;
}> = ({ ideaId, idea: preloadedIdea, evidenceId, singleLine = false, linkable = true, onAssign }) => {
  const { data: fetchedTitle, isLoading } = useQuery({
    queryKey: ["idea-title", ideaId],
    queryFn: async (): Promise<string | null> => (await getIdea(ideaId))?.title || null,
    enabled: !preloadedIdea,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const title: string | null = preloadedIdea ? preloadedIdea.title || null : fetchedTitle ?? null;
  const loading = !preloadedIdea && isLoading;
  const href = `/idea/${ideaId}${evidenceId ? `?evidenceId=${evidenceId}` : ""}`;
  const displayTitle = title || "Idea";

  if (loading) return <Skeleton height={32} radius="md" />;

  return (
    <Tooltip label={displayTitle} withinPortal multiline maw={260} openDelay={300} disabled={!title || title.length < 45}>
      <Flex
        {...(linkable ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" } : {})}
        align="center"
        justify="space-between"
        gap={8}
        px={10}
        py={7}
        sx={{
          textDecoration: "none",
          borderRadius: "6px",
          border: "0px solid #E5E7EB",
          background: "#FAFAFA",
          ...(linkable && { "&:hover": { background: "#F0F0FF", borderColor: "#C7D2FE" } }),
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Text
          fz="13px"
          fw={500}
          color="#4F46E5"
          sx={{
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: singleLine ? 1 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.4,
          }}
        >
          {displayTitle}
        </Text>
        {linkable && (
          <Tooltip label="Open idea in new tab" withinPortal>
            <span style={{ display: "flex", flexShrink: 0 }}>
              <IconExternalLink size={13} color="#6366F1" />
            </span>
          </Tooltip>
        )}
        {onAssign && (
          <Tooltip label="Assign to this idea" withinPortal>
            <ActionIcon
              size={20}
              radius="sm"
              color=""
              style={{ flexShrink: 0, marginLeft: 4 }}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onAssign(ideaId, displayTitle);
              }}
            >
              <Text size={11} color="#4F46E5">Assign</Text>
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>
    </Tooltip>
  );
};

const AssignConfirmCard: React.FC<{
  ideaId: string;
  ideaTitle: string;
  evidenceId: string;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ ideaId, ideaTitle, evidenceId, onClose, onSuccess }) => {
  const { validate, isLoading } = useEvidenceActions({ evidenceId });
  const ideaHref = `/idea/${ideaId}`;

  const handleConfirm = async () => {
    const res = await validate({ ideaId });
    if (res?.success) {
      showNotification({ title: "Evidence assigned successfully", color: "teal", message: "", icon: <IconCheck size="1.1rem" />, autoClose: 1200 });
      onClose();
      onSuccess?.();
    } else {
      showNotification({ title: "Error assigning evidence", color: "red", message: "Please try again", icon: <IconX size="1.1rem" />, autoClose: 1200 });
    }
  };

  return (
    <Paper
      p={16}
      radius="md"
      shadow="md"
      sx={{ border: "1px solid #E5E7EB", background: "#fff" }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Text fw={700} fz={15} mb={10}>Confirm assignment</Text>
      <MantineDivider mb={10} />
      <Text fz={13} mb={14}>
        The evidence will be assigned to this idea:{" "}
        <Text
          component="a"
          href={ideaHref}
          target="_blank"
          rel="noopener noreferrer"
          fz={13}
          color="indigo"
          sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          {ideaTitle}
        </Text>
      </Text>
      <Button color="indigo" mr={8} onClick={handleConfirm} loading={isLoading.validate} styles={{ root: { height: 32 } }}>
        Confirm
      </Button>
      <Button variant="subtle" color="gray" onClick={onClose} styles={{ root: { height: 32, color: "#6B7280" } }}>
        Cancel
      </Button>
    </Paper>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const AssignedToIdeaTag: React.FC<AssignedToIdeaTagProps> = ({ evidence, compact = false }) => {
  const { id: pageIdeaId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const [confirmAssign, setConfirmAssign] = useState<{ ideaId: string; ideaTitle: string } | null>(null);
  // Without this permission, idea titles are plain text and there is no Assign action
  const { canOpenEvidencePopup } = useUserPermissions();

  const caseDisplay = getCaseDisplay(evidence, pageIdeaId);
  if (!caseDisplay) return null;

  const { caseType, icon: tagIcon, tagLabel, popupHeader: popupSectionLabel } = caseDisplay;

  const ad = evidence?.autoAssignDecision;
  const reason: string | undefined = ad?.reason;
  // ideaIds  comes from autoAssignDecision.ideaIds directly (not parsed from reason text)
  const ideaIds: string[] = (ad?.ideaIds || []).map(toIdeaId).filter((id: string | undefined): id is string => !!id);

  const ideaId = toIdeaId(evidence?.ideaId);

  const isGap = caseType === AssignmentCase.CannotDecide;
  const isInReview = caseType === AssignmentCase.InReview;

  return (
    <HoverCard width={290} position="top" withArrow shadow="md" openDelay={150} closeDelay={100} withinPortal>
      <HoverCard.Target>
        <Flex
          align="center"
          gap={5}
          h={compact ? 24 : 32}
          px={compact ? 8 : "sm"}
          sx={{
            cursor: "default",
            borderRadius: "5px",
            background: "#F8F9FB",
            border: `1px solid ${"#D9D6FB"}`,
            flexShrink: 0,
            "&:hover": { background:  "#E4E2FB" },
          }}
        >
          {tagIcon}
          <Text fz={compact ? "11px" : "13px"} fw={500} color="#4B44D6" sx={{ whiteSpace: "nowrap" }}>
            {tagLabel}
          </Text>
        </Flex>
      </HoverCard.Target>

      <HoverCard.Dropdown
        sx={{
          borderRadius: "10px",
          padding: "14px 16px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 8px 24px rgba(17,24,39,0.12)",
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* ── Not A Product Gap (decline_not_a_request) ── */}
        {isInReview ? (
          <>
            <Text fz="14px" fw={700} color="#111827" mb={8}>Not A Product Gap</Text>
            {reason && (
              <>
                <Text fz="12px" fw={600} color="#6B7280" mt={6} mb={2}>Why:</Text>
                <Box sx={{ maxHeight: "calc(5 * 13px * 1.6)", overflowY: "auto" }}>
                  <Text fz="13px" color="#374151" lh={1.6}>{reason}</Text>
                </Box>
              </>
            )}
          </>
        ) : (
          <SectionLabel>{popupSectionLabel}</SectionLabel>
        )}

        {/* ── Cannot Decide: idea matches from ideaIds list + reason ── */}
        {isGap && (
          <>
            {ideaIds.length > 0 && (
              <Flex direction="column" gap={6}>
                {ideaIds.filter((id) => id !== pageIdeaId).slice(0, 2).map((id) => (
                  <IdeaLinkRow
                    key={id}
                    ideaId={id}
                    evidenceId={evidence?._id}
                    linkable={canOpenEvidencePopup}
                    onAssign={canOpenEvidencePopup ? (iId, iTitle) => setConfirmAssign({ ideaId: iId, ideaTitle: iTitle }) : undefined}
                  />
                ))}
              </Flex>
            )}
            {reason && <ReasonBlock reason={reason} withDivider={ideaIds.length > 0} />}
          </>
        )}

        {/* ── AI Assign / Create: idea link + optional reason ── */}
        {(caseType === AssignmentCase.AiAssign || caseType === AssignmentCase.AiCreate) && (
          <>
            {ideaId && ideaId !== pageIdeaId && (
              <IdeaLinkRow ideaId={ideaId} idea={evidence?.idea} evidenceId={evidence?._id} singleLine linkable={canOpenEvidencePopup} />
            )}
            {!ideaId && ideaIds.length > 0 && (
              <Flex direction="column" gap={6}>
                {ideaIds.filter((id) => id !== pageIdeaId).slice(0, 2).map((id) => (
                  <IdeaLinkRow
                    key={id}
                    ideaId={id}
                    evidenceId={evidence?._id}
                    linkable={canOpenEvidencePopup}
                  />
                ))}
              </Flex>
            )}
            {reason && <ReasonBlock reason={reason} />}
          </>
        )}

        {/* ── User / PM: idea link ── */}
        {(caseType === AssignmentCase.User || caseType === AssignmentCase.Pm) && (
          <>
            {ideaId && ideaId !== pageIdeaId && (
              <IdeaLinkRow ideaId={ideaId} idea={evidence?.idea} evidenceId={evidence?._id} singleLine linkable={canOpenEvidencePopup} />
            )}
            {!ideaId && evidence?.idea?.title && (
              <Flex
                align="center"
                justify="space-between"
                gap={8}
                px={10}
                py={7}
                sx={{ borderRadius: "6px", border: "1px solid #E5E7EB", background: "#FAFAFA" }}
              >
                <Text
                  fz="13px"
                  fw={500}
                  color="#4F46E5"
                  sx={{ flex: 1, WebkitLineClamp: 1, display: "-webkit-box", WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                  {evidence.idea.title}
                </Text>
                {canOpenEvidencePopup && <IconExternalLink size={13} color="#6366F1" style={{ flexShrink: 0 }} />}
              </Flex>
            )}
            {reason && <ReasonBlock reason={reason} />}
          </>
        )}

        {/* ── Confirm assign overlay ── */}
        {confirmAssign && evidence?._id && (
          <Box
            sx={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "flex-start",
              padding: "8px",
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <AssignConfirmCard
              ideaId={confirmAssign.ideaId}
              ideaTitle={confirmAssign.ideaTitle}
              evidenceId={evidence._id}
              onClose={() => setConfirmAssign(null)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ["evidences", "list"] });
                queryClient.invalidateQueries({ queryKey: ["evidence-view-count"] });
                queryClient.invalidateQueries({ queryKey: ["evidences-bulk"] });
                queryClient.invalidateQueries({ queryKey: ["explore-insights-idea-evidences-assigned"] });
                queryClient.invalidateQueries({ queryKey: ["explore-insights-idea-evidences-suggested"] });
              }}
            />
          </Box>
        )}
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default AssignedToIdeaTag;
